/**
 * Central role + portal definitions. Keep in sync with:
 *   backend/auth.py::PORTAL_ROLES, ROLE_HOME
 *   backend/stores.py::GLOBAL_ROLES
 *   backend/admin.py::ADMIN_ROLES
 *
 * TSGo has six strict roles with 1:1 login portals:
 *   super_admin       — /superadmin/login → /admin/dashboard
 *   operations_admin  — /ops/login        → /ops/dashboard
 *   store_admin       — /store/login      → /store/dashboard
 *   staff             — /staff/login      → /staff/dashboard
 *   rider             — /rider/login      → /rider/dashboard
 *   customer          — /login            → /home
 *
 * The legacy 'admin' seed role is treated as super_admin server-side and
 * is no longer emitted — the migration in init_auth removes those users.
 */

export type UserRole =
    | "customer"
    | "rider"
    | "staff"
    | "store_admin"
    | "operations_admin"
    | "super_admin";

export type PortalKey =
    | "customer"
    | "rider"
    | "staff"
    | "store"
    | "ops"
    | "superadmin";

export const ROLE_HOME: Record<UserRole, string> = {
    customer: "/home",
    rider: "/rider/dashboard",
    staff: "/staff/dashboard",
    store_admin: "/store/dashboard",
    operations_admin: "/ops/dashboard",
    super_admin: "/admin/dashboard",
};

export const PORTAL_ROLE: Record<PortalKey, UserRole> = {
    customer: "customer",
    rider: "rider",
    staff: "staff",
    store: "store_admin",
    ops: "operations_admin",
    superadmin: "super_admin",
};

export const PORTAL_LOGIN_PATH: Record<PortalKey, string> = {
    customer: "/login",
    rider: "/rider/login",
    staff: "/staff/login",
    store: "/store/login",
    ops: "/ops/login",
    superadmin: "/superadmin/login",
};

// Roles that see across every store (no store_id filter)
export const GLOBAL_ROLES: readonly UserRole[] = [
    "super_admin",
    "operations_admin",
] as const;

// Every admin-flavoured role — can access /admin/* legacy routes if allowed
export const ADMIN_ROLES: readonly UserRole[] = [
    "super_admin",
    "operations_admin",
    "store_admin",
    "staff",
] as const;

export function isAdminRole(role: string | null | undefined): boolean {
    if (!role) return false;
    // 'admin' is a legacy alias — should never appear post-migration, but
    // treat it as super_admin defensively.
    if (role === "admin") return true;
    return (ADMIN_ROLES as readonly string[]).includes(role);
}

export function isGlobalRole(role: string | null | undefined): boolean {
    if (!role) return false;
    if (role === "admin") return true;
    return (GLOBAL_ROLES as readonly string[]).includes(role);
}

export function roleLabel(role: string | null | undefined): string {
    if (!role) return "";
    if (role === "customer") return "Customer";
    if (role === "admin") return "Super Admin";
    return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function homeForRole(role: string | null | undefined): string {
    if (!role) return "/login";
    if (role === "admin") return ROLE_HOME.super_admin;
    return ROLE_HOME[role as UserRole] ?? "/home";
}
